import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Mail, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import Logo from '@/components/Logo';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const { user, getAuthHeader, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/verify-email`, { code }, { headers: getAuthHeader() });
      toast.success('Email verified! Your free trial has started.');
      await refreshUser();
      navigate('/profile?welcome=1');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, {}, { headers: getAuthHeader() });
      toast.success('New code sent to your email');
    } catch (err) {
      toast.error('Failed to resend code');
    } finally { setResending(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <SEO title="Verify Your Email" description="Verify your Poettik account email to start your free trial." noindex />
      <Card className="w-full max-w-md" data-testid="verify-email-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Logo size="lg" />
          </div>
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Mail className="w-7 h-7 text-primary" />
          </div>
          <CardTitle className="text-xl">Verify Your Email</CardTitle>
          <p className="text-sm text-muted-foreground mt-2">
            We sent a 6-digit code to <strong>{user?.email}</strong>. Enter it below to start your free trial.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleVerify} className="space-y-4">
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className="text-center text-2xl tracking-[0.5em] font-mono"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              data-testid="verify-code-input"
            />
            <Button
              type="submit"
              variant="premium"
              className="w-full"
              disabled={loading || code.length !== 6}
              data-testid="verify-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Verify Email
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="resend-code-btn"
            >
              {resending ? 'Sending...' : "Didn't receive it? Resend code"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
